import React, { useState } from "react";
import {
  Box,
  Heading,
  Text,
  Button,
  VStack,
  HStack,
  SimpleGrid,
  Card,
  CardBody,
  Badge,
  Container,
  useColorModeValue,
  Link,
  List,
  ListItem,
  Flex,
  Avatar,
  AvatarGroup,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Divider,
  Grid,
  GridItem,
  Code,
} from "@chakra-ui/react";
import Icon from "components/icons/Icon";
import {
  FiMonitor,
  FiSmartphone,
  FiDownload,
  FiArrowRight,
  FiCheck,
  FiUsers,
  FiBriefcase,
  FiHeart,
  FiShield,
  FiTrendingUp,
  FiZap,
  FiStar,
  FiAward,
  FiTarget,
  FiBarChart,
  FiUserPlus,
  FiSettings,
  FiEye,
  FiCalendar,
  FiDatabase,
  FiLink,
  FiTrash2,
  FiUserCheck,
  FiLayers,
  FiFileText,
  FiUser,
  FiActivity,
  FiBarChart2,
  FiMessageCircle,
  FiKey,
  FiLock,
  FiShare2,
  FiGlobe,
} from "react-icons/fi";
import { FaGooglePlay, FaApple } from "react-icons/fa";
import { MdBusiness, MdFitnessCenter, MdPerson } from "react-icons/md";
import { motion } from "framer-motion";

const MotionBox = motion(Box);

function PublicGettingStarted() {
  const [activeOnboarding, setActiveOnboarding] = useState<'agency' | 'trainer' | 'client'>('agency');
  
  // Consistent Design System
  const bgColor = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const textColor = useColorModeValue("gray.600", "gray.300");
  const textColorSecondary = useColorModeValue("gray.500", "gray.400");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const gradientBg = useColorModeValue(
    "linear(to-br, blue.500, purple.600)",
    "linear(to-br, blue.400, purple.500)"
  );

  // Consistent spacing and styling
  const sectionSpacing = { base: 16, md: 20, lg: 24 };
  const containerMaxW = "1200px";
  const headingStyles = {
    fontWeight: "800",
    letterSpacing: "-0.02em",
    lineHeight: "1.1"
  };
  const cardStyles = {
    bg: "white",
    borderRadius: "2xl",
    boxShadow: "lg",
    border: "1px solid",
    borderColor: "gray.100",
    transition: "all 0.3s ease",
    _hover: {
      transform: "translateY(-4px)",
      boxShadow: "xl",
      borderColor: "blue.200"
    }
  };

  return (
    <Box bg={bgColor} minH="100vh">
      {/* Hero Section */}
      <Box
        bgGradient="linear(135deg, #f8fafc 0%, #e2e8f0 100%)"
        color="gray.800"
        position="relative"
        overflow="hidden"
        minH="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        {/* Subtle background pattern */}
        <Box
          position="absolute"
          top="0"
          left="0"
          right="0"
          bottom="0"
          opacity="0.3"
          bgImage="radial-gradient(circle at 20% 30%, rgba(59, 130, 246, 0.1) 0%, transparent 40%), radial-gradient(circle at 80% 70%, rgba(168, 85, 247, 0.1) 0%, transparent 40%)"
        />
        
        <Container maxW="6xl" mx="auto" px={{ base: 6, md: 8 }} position="relative" zIndex={1}>
          <VStack spacing={{ base: 12, md: 16, lg: 20 }} align="center" textAlign="center">
            {/* Badge */}
            <MotionBox
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Badge 
                bg="blue.100"
                color="blue.700"
                px={8} 
                py={3} 
                borderRadius="full"
                fontSize={{ base: "sm", md: "md" }}
                fontWeight="700"
                textTransform="uppercase"
                letterSpacing="0.1em"
                border="1px solid"
                borderColor="blue.200"
                shadow="sm"
              >
                PROFESSIONAL PLATFORM
              </Badge>
            </MotionBox>

            {/* Main Heading */}
            <MotionBox
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <VStack spacing={{ base: 2, md: 4 }} maxW="5xl">
                <Heading 
                  fontSize={{ base: "4xl", sm: "5xl", md: "6xl", lg: "7xl", xl: "8xl" }}
                  fontWeight="900"
                  letterSpacing="-0.05em"
                  lineHeight="0.9"
                  textAlign="center"
                >
                  <Text 
                    as="span"
                    color="gray.900"
                    display="inline"
                  >
                    Track
                  </Text>
                  <Text 
                    as="span"
                    bgGradient="linear(135deg, #3b82f6 0%, #1e40af 100%)"
                    bgClip="text"
                    display="inline"
                  >
                    HQ
                  </Text>
                
                </Heading>
                <Heading 
                  fontSize={{ base: "2xl", sm: "3xl", md: "4xl", lg: "5xl", xl: "6xl" }}
                  fontWeight="800"
                  letterSpacing="-0.03em"
                  lineHeight="1.1"
                  color="gray.700"
                  mt={4}
                >
                  Fitness Management Platform
                </Heading>
              </VStack>
            </MotionBox>
              
            {/* Description */}
            <MotionBox
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <Text 
                fontSize={{ base: "lg", md: "xl", lg: "2xl" }}
                color="gray.600" 
                fontWeight="500"
                lineHeight="1.6"
                maxW={{ base: "95%", md: "4xl" }}
                textAlign="center"
              >
                Complete multi-tenant fitness management solution for agencies, 
                trainers, and clients with advanced {" "}
                <Text as="span" color="blue.600" fontWeight="600">
                  workout planning
                </Text>
              </Text>
            </MotionBox>

            {/* Role Tags */}
            <MotionBox
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              <VStack spacing={6} pt={{ base: 4, md: 8 }}>
                <HStack spacing={{ base: 4, md: 8 }} flexWrap="wrap" justify="center">
                  <Button
                    leftIcon={<Icon as={FiBriefcase} />}
                    bg="white"
                    color="blue.700"
                    border="2px solid"
                    borderColor="blue.200"
                    px={{ base: 4, md: 6 }}
                    py={{ base: 4, md: 5 }}
                    h="auto"
                    borderRadius="full"
                    fontSize={{ base: "sm", md: "md" }}
                    fontWeight="700"
                    shadow="md"
                    _hover={{
                      bg: "blue.50",
                      borderColor: "blue.300",
                      transform: "translateY(-2px)",
                      shadow: "lg"
                    }}
                    _active={{
                      transform: "translateY(0px)"
                    }}
                    transition="all 0.2s ease"
                  >
                    Agencies
                  </Button>
                  
                  <Button
                    leftIcon={<Icon as={FiUsers} />}
                    bg="white"
                    color="purple.700"
                    border="2px solid"
                    borderColor="purple.200"
                    px={{ base: 4, md: 6 }}
                    py={{ base: 4, md: 5 }}
                    h="auto"
                    borderRadius="full"
                    fontSize={{ base: "sm", md: "md" }}
                    fontWeight="700"
                    shadow="md"
                    _hover={{
                      bg: "purple.50",
                      borderColor: "purple.300",
                      transform: "translateY(-2px)",
                      shadow: "lg"
                    }}
                    _active={{
                      transform: "translateY(0px)"
                    }}
                    transition="all 0.2s ease"
                  >
                    Trainers
                  </Button>
                  
                  <Button
                    leftIcon={<Icon as={FiHeart} />}
                    bg="white"
                    color="green.700"
                    border="2px solid"
                    borderColor="green.200"
                    px={{ base: 4, md: 6 }}
                    py={{ base: 4, md: 5 }}
                    h="auto"
                    borderRadius="full"
                    fontSize={{ base: "sm", md: "md" }}
                    fontWeight="700"
                    shadow="md"
                    _hover={{
                      bg: "green.50",
                      borderColor: "green.300",
                      transform: "translateY(-2px)",
                      shadow: "lg"
                    }}
                    _active={{
                      transform: "translateY(0px)"
                    }}
                    transition="all 0.2s ease"
                  >
                    Clients
                  </Button>
                </HStack>
              </VStack>
            </MotionBox>
          </VStack>
        </Container>
      </Box>

      {/* Getting Started Complete Guide Section */}
      <Box py={sectionSpacing} px={{ base: 4, md: 8 }} bg="gray.50">
        <Container maxW={containerMaxW} mx="auto">
          <VStack spacing={{ base: 12, md: 16 }} align="center">
            <VStack spacing={6} textAlign="center" maxW="4xl">
              <Badge 
                bg="blue.100"
                color="blue.700"
                px={6} 
                py={2} 
                borderRadius="full"
                fontSize="md"
                fontWeight="700"
                textTransform="uppercase"
                letterSpacing="wider"
                border="1px solid"
                borderColor="blue.200"
              >
                COMPLETE GUIDE
              </Badge>
              <Heading 
                fontSize={{ base: "3xl", md: "4xl", lg: "5xl" }}
                color="gray.800"
                {...headingStyles}
              >
                Complete Getting Started Guide
              </Heading>
              <Text 
                fontSize={{ base: "lg", md: "xl" }}
                color={textColor} 
                lineHeight="1.7" 
                maxW="3xl"
                textAlign="center"
              >
                Comprehensive onboarding workflows for Agencies, Trainers, and Clients based on actual platform implementation
              </Text>
              {/* Toggle Buttons */}
              <HStack spacing={6} justify="center" flexWrap="wrap" pt={6}>
                <Button
                  size="lg"
                  px={8}
                  py={4}
                  h="auto"
                  bg={activeOnboarding === 'agency' ? 'blue.500' : 'white'}
                  color={activeOnboarding === 'agency' ? 'white' : 'gray.700'}
                  border="2px solid"
                  borderColor={activeOnboarding === 'agency' ? 'blue.500' : 'gray.200'}
                  borderRadius="xl"
                  fontWeight="700"
                  shadow="md"
                  _hover={{
                    bg: activeOnboarding === 'agency' ? 'blue.600' : 'blue.50',
                    borderColor: 'blue.400',
                    transform: "translateY(-2px)",
                    shadow: "lg"
                  }}
                  _active={{
                    transform: "translateY(0px)"
                  }}
                  transition="all 0.3s ease"
                  leftIcon={<Icon as={MdBusiness} boxSize={5} />}
                  onClick={() => setActiveOnboarding('agency')}
                >
                  Agency Process
                </Button>
                
                <Button
                  size="lg"
                  px={8}
                  py={4}
                  h="auto"
                  bg={activeOnboarding === 'trainer' ? 'green.500' : 'white'}
                  color={activeOnboarding === 'trainer' ? 'white' : 'gray.700'}
                  border="2px solid"
                  borderColor={activeOnboarding === 'trainer' ? 'green.500' : 'gray.200'}
                  borderRadius="xl"
                  fontWeight="700"
                  shadow="md"
                  _hover={{
                    bg: activeOnboarding === 'trainer' ? 'green.600' : 'green.50',
                    borderColor: 'green.400',
                    transform: "translateY(-2px)",
                    shadow: "lg"
                  }}
                  _active={{
                    transform: "translateY(0px)"
                  }}
                  transition="all 0.3s ease"
                  leftIcon={<Icon as={MdFitnessCenter} boxSize={5} />}
                  onClick={() => setActiveOnboarding('trainer')}
                >
                  Trainer Process
                </Button>
                
                <Button
                  size="lg"
                  px={8}
                  py={4}
                  h="auto"
                  bg={activeOnboarding === 'client' ? 'purple.500' : 'white'}
                  color={activeOnboarding === 'client' ? 'white' : 'gray.700'}
                  border="2px solid"
                  borderColor={activeOnboarding === 'client' ? 'purple.500' : 'gray.200'}
                  borderRadius="xl"
                  fontWeight="700"
                  shadow="md"
                  _hover={{
                    bg: activeOnboarding === 'client' ? 'purple.600' : 'purple.50',
                    borderColor: 'purple.400',
                    transform: "translateY(-2px)",
                    shadow: "lg"
                  }}
                  _active={{
                    transform: "translateY(0px)"
                  }}
                  transition="all 0.3s ease"
                  leftIcon={<Icon as={MdPerson} boxSize={5} />}
                  onClick={() => setActiveOnboarding('client')}
                >
                  Client Process
                </Button>
              </HStack>
            </VStack>

            {/* Agency Onboarding Section */}
            {activeOnboarding === 'agency' && (
              <Box w="100%" maxW="6xl" mb={16}>
                <VStack spacing={8} textAlign="center" mb={12}>
                  <Badge 
                    bg="blue.100"
                    color="blue.700"
                    px={4} 
                    py={2} 
                    borderRadius="full"
                    fontSize="sm"
                    fontWeight="700"
                    textTransform="uppercase"
                    letterSpacing="wider"
                    border="1px solid"
                    borderColor="blue.200"
                  >
                    AGENCY PROCESS
                  </Badge>
                  <Heading 
                    fontSize={{ base: "2xl", md: "3xl" }}
                    color="gray.800"
                    {...headingStyles}
                  >
                    🏢 Agency Onboarding Process
                  </Heading>
                </VStack>
              
              <Box position="relative">
                {/* Central Timeline Line */}
                <Box
                  position="absolute"
                  left="50%"
                  top="0"
                  bottom="0"
                  w="2px"
                  bg="gray.200"
                  transform="translateX(-50%)"
                  display={{ base: "none", lg: "block" }}
                />
                
                <VStack spacing={{ base: 8, md: 12 }} w="100%">
                  {[
                    {
                      phase: "Step 1",
                      title: "Agency Registration",
                      description: "Complete agency signup with business name, description, address, and contact information. Creates owner account with pending approval status.",
                      icon: FiBriefcase,
                      color: "blue",
                      side: "left",
                      features: ["Business profile creation", "Owner account creation", "Contact information", "Application submission"]
                    },
                    {
                      phase: "Step 2", 
                      title: "Admin Approval",
                      description: "Platform administrators review and approve agency applications. Verification of business details, legitimacy checks, and compliance validation before activation.",
                      icon: FiShield,
                      color: "purple",
                      side: "right",
                      features: ["Application review", "Business verification", "Compliance checks", "Account activation"]
                    },
                    {
                      phase: "Step 3",
                      title: "Agency Code Generation",
                      description: "After approval, receive unique agency code for trainer recruitment. Share this code with certified trainers to join your agency with automatic agency linking and data isolation.",
                      icon: FiKey,
                      color: "green",
                      side: "left",
                      features: ["Unique agency identifier", "Trainer recruitment tool", "Automatic agency linking", "Secure code sharing"]
                    },
                    {
                      phase: "Step 4",
                      title: "Trainer Management",
                      description: "Monitor trainer signups, manage trainer profiles, view specializations and certifications. Access trainer performance metrics and client assignment tracking.",
                      icon: FiUsers,
                      color: "orange",
                      side: "right",
                      features: ["Trainer profile oversight", "Certification tracking", "Performance analytics", "Client assignment monitoring"]
                    },
                    {
                      phase: "Step 5",
                      title: "Business Operations", 
                      description: "Access comprehensive agency dashboard with trainer statistics, client engagement metrics, workout plan analytics, and business performance insights.",
                      icon: FiBarChart,
                      color: "teal",
                      side: "left",
                      features: ["Agency dashboard", "Business analytics", "Trainer productivity", "Client engagement metrics"]
                    }
                  ].map((item, index) => (
                    <MotionBox
                      key={index}
                      initial={{ opacity: 0, y: 50 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: index * 0.2 }}
                    >
                      <Flex
                        direction={{ base: "column", lg: item.side === 'left' ? "row" : "row-reverse" }}
                        align="center"
                        w="100%"
                        position="relative"
                      >
                        {/* Timeline Number - Desktop */}
                        <Box
                          position="absolute"
                          left="50%"
                          transform="translateX(-50%)"
                          bg={`${item.color}.500`}
                          color="white"
                          borderRadius="full"
                          w="16"
                          h="16"
                          display={{ base: "none", lg: "flex" }}
                          alignItems="center"
                          justifyContent="center"
                          fontWeight="bold"
                          fontSize="lg"
                          boxShadow="lg"
                          zIndex={2}
                        >
                          {index + 1}
                        </Box>
                        
                        {/* Content */}
                        <Box flex={1} pr={{ base: 0, lg: item.side === 'left' ? 12 : 0 }} pl={{ base: 0, lg: item.side === 'right' ? 12 : 0 }} mb={{ base: 6, lg: 0 }}>
                          <Card
                            bg={cardBg}
                            borderRadius="3xl"
                            p={{ base: 6, md: 8 }}
                            border="1px solid"
                            borderColor={borderColor}
                            boxShadow="xl"
                            transition="all 0.4s ease"
                            _hover={{
                              transform: "translateY(-4px)",
                              boxShadow: "2xl",
                            }}
                          >
                            <VStack align="start" spacing={5}>
                              <HStack spacing={4}>
                                <Box
                                  bg={`${item.color}.500`}
                                  p={3}
                                  borderRadius="2xl"
                                  color="white"
                                >
                                  <Icon as={item.icon} boxSize={7} />
                                </Box>
                                <VStack align="start" spacing={1}>
                                  <Badge 
                                    colorScheme={item.color} 
                                    variant="solid" 
                                    borderRadius="full"
                                    px={4}
                                    py={1}
                                    fontSize="xs"
                                    fontWeight="700"
                                  >
                                    {item.phase}
                                  </Badge>
                                  <Heading fontSize={{ base: "xl", md: "2xl" }} color="gray.800" fontWeight="800">
                                    {item.title}
                                  </Heading>
                                </VStack>
                              </HStack>
                              
                              <Text color={textColor} fontSize={{ base: "md", md: "lg" }} fontWeight="500" lineHeight="1.6">
                                {item.description}
                              </Text>
                              
                              <SimpleGrid columns={2} spacing={2} w="100%">
                                {item.features.map((feature, idx) => (
                                  <HStack key={idx} spacing={2}>
                                    <Icon as={FiCheck} color={`${item.color}.500`} boxSize={3} />
                                    <Text fontSize={{ base: "xs", md: "sm" }} color={textColorSecondary}>
                                      {feature}
                                    </Text>
                                  </HStack>
                                ))}
                              </SimpleGrid>
                            </VStack>
                          </Card>
                        </Box>

                        {/* Spacer for opposite side */}
                        <Box flex={1} display={{ base: "none", lg: "block" }} />
                      </Flex>
                    </MotionBox>
                  ))}
                </VStack>
              </Box>
            </Box>
            )}

            {/* Trainer Onboarding Section */}
            {activeOnboarding === 'trainer' && (
              <Box w="100%" maxW="6xl" mb={16}>
                <VStack spacing={8} textAlign="center" mb={12}>
                  <Badge 
                    bg="green.100"
                    color="green.700"
                    px={4} 
                    py={2} 
                    borderRadius="full"
                    fontSize="sm"
                    fontWeight="700"
                    textTransform="uppercase"
                    letterSpacing="wider"
                    border="1px solid"
                    borderColor="green.200"
                  >
                    TRAINER PROCESS
                  </Badge>
                  <Heading 
                    fontSize={{ base: "2xl", md: "3xl" }}
                    color="gray.800"
                    {...headingStyles}
                  >
                    💪 Trainer Onboarding Process
                  </Heading>
                </VStack>
              
              <Box position="relative">
                {/* Central Timeline Line */}
                <Box
                  position="absolute"
                  left="50%"
                  top="0"
                  bottom="0"
                  w="2px"
                  bg="gray.200"
                  transform="translateX(-50%)"
                  display={{ base: "none", lg: "block" }}
                />
                
                <VStack spacing={{ base: 12, md: 16, lg: 20 }} w="100%">
                  {[
                    {
                      phase: "Step 1",
                      title: "Receive Signup Link",
                      description: "Agency sends you a personalized signup link with their agency code. This link connects you directly to their fitness business and enables automatic account linking.",
                      icon: FiLink,
                      color: "blue",
                      side: "left",
                      features: ["Personalized signup link", "Agency code included", "Direct connection", "Automatic linking"]
                    },
                    {
                      phase: "Step 2",
                      title: "Complete Profile & Signup",
                      description: "Complete your professional trainer profile with specializations, certifications, experience, and contact information. Submit your application to join the agency.",
                      icon: FiUserPlus,
                      color: "green", 
                      side: "right",
                      features: ["Professional profile", "Certification details", "Experience information", "Application submission"]
                    },
                    {
                      phase: "Step 3",
                      title: "Trainer Code Generated",
                      description: "After successful signup, receive your unique trainer code to share with clients. Clients use this code to connect and access your personalized workout plans.",
                      icon: FiKey,
                      color: "purple",
                      side: "left",
                      features: ["Unique trainer code", "Client connection tool", "Workout plan access", "Professional identity"]
                    },
                    {
                      phase: "Step 4",
                      title: "Share Code with Client",
                      description: "Share your trainer code with clients to establish secure connections. Clients enter this code to link with you and access their personalized fitness programs.",
                      icon: FiShare2,
                      color: "orange",
                      side: "right",
                      features: ["Code sharing system", "Client connection", "Secure linking", "Access management"]
                    },
                    {
                      phase: "Step 5",
                      title: "Create Workout for Personalized Group/Individual Client",
                      description: "Design and create customized workout plans for individual clients or specialized groups. Build comprehensive fitness programs tailored to specific goals and requirements.",
                      icon: FiActivity,
                      color: "teal",
                      side: "left",
                      features: ["Individual workout plans", "Group fitness programs", "Goal-based customization", "Progress tracking"]
                    }
                  ].map((item, index) => (
                    <MotionBox
                      key={index}
                      initial={{ opacity: 0, y: 50 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: index * 0.2 }}
                    >
                      <Flex
                        direction={{ base: "column", lg: item.side === 'left' ? "row" : "row-reverse" }}
                        align="center"
                        w="100%"
                        position="relative"
                      >
                        {/* Timeline Number - Desktop */}
                        <Box
                          position="absolute"
                          left="50%"
                          transform="translateX(-50%)"
                          bg={`${item.color}.500`}
                          color="white"
                          borderRadius="full"
                          w="16"
                          h="16"
                          display={{ base: "none", lg: "flex" }}
                          alignItems="center"
                          justifyContent="center"
                          fontWeight="bold"
                          fontSize="lg"
                          boxShadow="lg"
                          zIndex={2}
                        >
                          {index + 1}
                        </Box>
                        
                        {/* Content */}
                        <Box flex={1} pr={{ base: 0, lg: item.side === 'left' ? 12 : 0 }} pl={{ base: 0, lg: item.side === 'right' ? 12 : 0 }} mb={{ base: 6, lg: 0 }}>
                          <Card
                            bg={cardBg}
                            borderRadius="3xl"
                            p={{ base: 6, md: 8 }}
                            border="1px solid"
                            borderColor={borderColor}
                            boxShadow="xl"
                            transition="all 0.4s ease"
                            _hover={{
                              transform: "translateY(-4px)",
                              boxShadow: "2xl",
                            }}
                          >
                            <VStack align="start" spacing={5}>
                              <HStack spacing={4}>
                                <Box
                                  bg={`${item.color}.500`}
                                  p={3}
                                  borderRadius="2xl"
                                  color="white"
                                >
                                  <Icon as={item.icon} boxSize={7} />
                                </Box>
                                <VStack align="start" spacing={1}>
                                  <Badge 
                                    colorScheme={item.color} 
                                    variant="solid" 
                                    borderRadius="full"
                                    px={4}
                                    py={1}
                                    fontSize="xs"
                                    fontWeight="700"
                                  >
                                    {item.phase}
                                  </Badge>
                                  <Heading fontSize={{ base: "xl", md: "2xl" }} color="gray.800" fontWeight="800">
                                    {item.title}
                                  </Heading>
                                </VStack>
                              </HStack>
                              
                              <Text color={textColor} fontSize={{ base: "md", md: "lg" }} fontWeight="500" lineHeight="1.6">
                                {item.description}
                              </Text>
                              
                              <SimpleGrid columns={2} spacing={2} w="100%">
                                {item.features.map((feature, idx) => (
                                  <HStack key={idx} spacing={2}>
                                    <Icon as={FiCheck} color={`${item.color}.500`} boxSize={3} />
                                    <Text fontSize={{ base: "xs", md: "sm" }} color={textColorSecondary}>
                                      {feature}
                                    </Text>
                                  </HStack>
                                ))}
                              </SimpleGrid>
                            </VStack>
                          </Card>
                        </Box>

                        {/* Spacer for opposite side */}
                        <Box flex={1} display={{ base: "none", lg: "block" }} />
                      </Flex>
                    </MotionBox>
                  ))}
                </VStack>
              </Box>
            </Box>
            )}

            {/* Client Onboarding Section */}
            {activeOnboarding === 'client' && (
              <Box w="100%" maxW="6xl" mb={16}>
                <VStack spacing={8} textAlign="center" mb={12}>
                  <Badge 
                    bg="purple.100"
                    color="purple.700"
                    px={4} 
                    py={2} 
                    borderRadius="full"
                    fontSize="sm"
                    fontWeight="700"
                    textTransform="uppercase"
                    letterSpacing="wider"
                    border="1px solid"
                    borderColor="purple.200"
                  >
                    CLIENT PROCESS
                  </Badge>
                  <Heading 
                    fontSize={{ base: "2xl", md: "3xl" }}
                    color="gray.800"
                    {...headingStyles}
                  >
                    👤 Client Onboarding Process
                  </Heading>
                </VStack>
              
                <Box position="relative">
                  {/* Central Timeline Line */}
                  <Box
                    position="absolute"
                    left="50%"
                    top="0"
                    bottom="0"
                    w="2px"
                    bg="gray.200"
                    transform="translateX(-50%)"
                    display={{ base: "none", lg: "block" }}
                  />
                  
                <VStack spacing={{ base: 8, md: 12 }} w="100%">
                  {[
                    {
                      phase: "Step 1",
                      title: "Mobile App Download",
                      description: "Download TrackHQ mobile app and create account with basic information. Set up your fitness profile with personal details and initial goals.",
                      icon: FiSmartphone,
                      color: "blue",
                      side: "left",
                      features: ["App download", "Account creation", "Basic profile setup", "Goal setting"]
                    },
                    {
                      phase: "Step 2", 
                      title: "Trainer Code Entry",
                      description: "Enter trainer code received from your personal trainer to establish secure connection. This links you with your assigned trainer while maintaining data privacy.",
                      icon: FiKey,
                      color: "green",
                      side: "right",
                      features: ["Trainer code entry", "Secure connection", "Automatic linking", "Privacy protection"]
                    },
                    {
                      phase: "Step 3",
                      title: "Workout Access",
                      description: "Receive personalized workout plans created specifically for your goals by your trainer. Access structured daily workouts with exercise details and progress tracking.",
                      icon: FiActivity,
                      color: "purple",
                      side: "left",
                      features: ["Custom workout plans", "Daily workouts", "Exercise details", "Progress tracking"]
                    }
                  ].map((item, index) => (
                    <MotionBox
                      key={index}
                      initial={{ opacity: 0, y: 50 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: index * 0.2 }}
                    >
                      <Flex
                        direction={{ base: "column", lg: item.side === 'left' ? "row" : "row-reverse" }}
                        align="center"
                        w="100%"
                        position="relative"
                      >
                        {/* Timeline Number - Desktop */}
                        <Box
                          position="absolute"
                          left="50%"
                          transform="translateX(-50%)"
                          bg={`${item.color}.500`}
                          color="white"
                          borderRadius="full"
                          w="16"
                          h="16"
                          display={{ base: "none", lg: "flex" }}
                          alignItems="center"
                          justifyContent="center"
                          fontWeight="bold"
                          fontSize="lg"
                          boxShadow="lg"
                          zIndex={2}
                        >
                          {index + 1}
                        </Box>
                        
                        {/* Content */}
                        <Box flex={1} pr={{ base: 0, lg: item.side === 'left' ? 12 : 0 }} pl={{ base: 0, lg: item.side === 'right' ? 12 : 0 }} mb={{ base: 6, lg: 0 }}>
                          <Card
                            bg={cardBg}
                            borderRadius="3xl"
                            p={{ base: 6, md: 8 }}
                            border="1px solid"
                            borderColor={borderColor}
                            boxShadow="xl"
                            transition="all 0.4s ease"
                            _hover={{
                              transform: "translateY(-4px)",
                              boxShadow: "2xl",
                            }}
                          >
                            <VStack align="start" spacing={5}>
                              <HStack spacing={4}>
                                <Box
                                  bg={`${item.color}.500`}
                                  p={3}
                                  borderRadius="2xl"
                                  color="white"
                                >
                                  <Icon as={item.icon} boxSize={7} />
                                </Box>
                                <VStack align="start" spacing={1}>
                                  <Badge 
                                    colorScheme={item.color} 
                                    variant="solid" 
                                    borderRadius="full"
                                    px={4}
                                    py={1}
                                    fontSize="xs"
                                    fontWeight="700"
                                  >
                                    {item.phase}
                                  </Badge>
                                  <Heading fontSize={{ base: "xl", md: "2xl" }} color="gray.800" fontWeight="800">
                                    {item.title}
                                  </Heading>
                                </VStack>
                              </HStack>
                              
                              <Text color={textColor} fontSize={{ base: "md", md: "lg" }} fontWeight="500" lineHeight="1.6">
                                {item.description}
                              </Text>
                              
                              <SimpleGrid columns={2} spacing={2} w="100%">
                                {item.features.map((feature, idx) => (
                                  <HStack key={idx} spacing={2}>
                                    <Icon as={FiCheck} color={`${item.color}.500`} boxSize={3} />
                                    <Text fontSize={{ base: "xs", md: "sm" }} color={textColorSecondary}>
                                      {feature}
                                    </Text>
                                  </HStack>
                                ))}
                              </SimpleGrid>
                            </VStack>
                          </Card>
                        </Box>

                        {/* Spacer for opposite side */}
                        <Box flex={1} display={{ base: "none", lg: "block" }} />
                      </Flex>
                    </MotionBox>
                  ))}
                  </VStack>
                </Box>
              </Box>
            )}
              
            {/* Call to Action */}
              <Box textAlign="center" pt={{ base: 8, md: 12 }}>
                <VStack spacing={6}>
                  <Text 
                    fontSize={{ base: "lg", md: "xl" }}
                    color="gray.700"
                    fontWeight="600"
                    maxW="2xl"
                    lineHeight="1.6"
                  >
                    Ready to launch your fitness business? Start with any role today!
                  </Text>
                  <Flex gap={4} direction={{ base: "column", sm: "row" }}>
                    <Link href="/auth/agency-signup">
                      <Button
                        bgGradient="linear(135deg, blue.500, blue.600)"
                        color="white"
                        size="lg"
                        h="14"
                        px={8}
                        borderRadius="xl"
                        rightIcon={<Icon as={FiArrowRight} />}
                        _hover={{
                          bgGradient: "linear(135deg, blue.600, blue.700)",
                          transform: "translateY(-2px)",
                          shadow: "xl",
                        }}
                        transition="all 0.3s"
                        fontWeight="600"
                        fontSize="md"
                      >
                        Start Agency
                      </Button>
                    </Link>
                    <Link href="/auth/sign-in">
                      <Button
                        variant="outline"
                        borderColor="gray.300"
                        color="gray.600"
                        size="lg"
                        h="14"
                        px={8}
                        borderRadius="xl"
                        _hover={{
                          bg: "gray.50",
                          transform: "translateY(-2px)",
                        }}
                        transition="all 0.3s"
                        fontWeight="600"
                        fontSize="md"
                      >
                        Sign In
                      </Button>
                    </Link>
                  </Flex>
                </VStack>
              </Box>
          </VStack>
        </Container>
      </Box>

      {/* Core Features Section */}
      <Box py={sectionSpacing} px={{ base: 4, md: 8 }} bg="white" position="relative">
        <Container maxW={containerMaxW}>
          <VStack spacing={16}>
            <VStack spacing={6} textAlign="center">
              <Badge 
                bg="blue.100"
                color="blue.700"
                px={6} 
                py={2} 
                borderRadius="full"
                fontSize="md"
                fontWeight="700"
                textTransform="uppercase"
                letterSpacing="wider"
                border="1px solid"
                borderColor="blue.200"
              >
                🚀 CORE FEATURES
              </Badge>
              <Heading 
                fontSize={{ base: "3xl", md: "4xl", lg: "5xl" }}
                color="gray.800"
                {...headingStyles}
              >
                Everything You Need to
                <Text as="span" color="blue.600" display="block">
                  Manage Fitness Success
                </Text>
              </Heading>
              <Text 
                fontSize={{ base: "lg", md: "xl" }}
                color="gray.600" 
                maxW="3xl"
                lineHeight="1.7"
                textAlign="center"
              >
                Comprehensive tools tailored for each role in your fitness ecosystem
              </Text>
            </VStack>

            <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={8} w="100%">
              {/* Agency Features */}
              <MotionBox
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
              >
                <Box
                  {...cardStyles}
                  p={8}
                  borderColor="blue.100"
                  _hover={{ 
                    ...cardStyles._hover,
                    borderColor: "blue.300"
                  }}
                  h="100%"
                >
                  <VStack spacing={6} align="start" h="100%">
                    <Flex
                      w={16}
                      h={16}
                      align="center"
                      justify="center"
                      borderRadius="2xl"
                      bgGradient="linear(135deg, blue.500, blue.600)"
                      color="white"
                      shadow="lg"
                    >
                      <Icon as={FiUsers} boxSize={8} />
                    </Flex>
                    
                    <VStack spacing={4} align="start" flex={1}>
                      <Badge colorScheme="blue" variant="subtle" px={3} py={1} borderRadius="full">
                        Agency Dashboard
                      </Badge>
                      
                      <Heading size="lg" color="gray.800" fontWeight="800">
                        Agency Management
                      </Heading>
                      
                      <Text color="gray.600" lineHeight="1.7" fontSize="md">
                        Complete control over your fitness business operations
                      </Text>
                      
                      <VStack spacing={3} align="start" w="100%">
                        <HStack spacing={3}>
                          <Box w={2} h={2} borderRadius="full" bg="blue.500" />
                          <Text fontSize="sm" color="gray.700">
                            <strong>Trainer Management:</strong> Create signup links, activate/deactivate trainers
                          </Text>
                        </HStack>
                        <HStack spacing={3}>
                          <Box w={2} h={2} borderRadius="full" bg="blue.500" />
                          <Text fontSize="sm" color="gray.700">
                            <strong>Business Analytics:</strong> View trainer and client statistics
                          </Text>
                        </HStack>
                        <HStack spacing={3}>
                          <Box w={2} h={2} borderRadius="full" bg="blue.500" />
                          <Text fontSize="sm" color="gray.700">
                            <strong>Exercise Library:</strong> Create and manage exercises for all trainers
                          </Text>
                        </HStack>
                        <HStack spacing={3}>
                          <Box w={2} h={2} borderRadius="full" bg="blue.500" />
                          <Text fontSize="sm" color="gray.700">
                            <strong>Platform Oversight:</strong> Monitor trainer-client relationships
                          </Text>
                        </HStack>
                      </VStack>
                    </VStack>
                  </VStack>
                </Box>
              </MotionBox>

              {/* Trainer Features */}
              <MotionBox
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <Box
                  {...cardStyles}
                  p={8}
                  borderColor="green.100"
                  _hover={{ 
                    ...cardStyles._hover,
                    borderColor: "green.300"
                  }}
                  h="100%"
                >
                  <VStack spacing={6} align="start" h="100%">
                    <Flex
                      w={16}
                      h={16}
                      align="center"
                      justify="center"
                      borderRadius="2xl"
                      bgGradient="linear(135deg, green.500, green.600)"
                      color="white"
                      shadow="lg"
                    >
                      <Icon as={FiTarget} boxSize={8} />
                    </Flex>
                    
                    <VStack spacing={4} align="start" flex={1}>
                      <Badge colorScheme="green" variant="subtle" px={3} py={1} borderRadius="full">
                        Trainer Dashboard
                      </Badge>
                      
                      <Heading size="lg" color="gray.800" fontWeight="800">
                        Professional Training Tools
                      </Heading>
                      
                      <Text color="gray.600" lineHeight="1.7" fontSize="md">
                        Everything needed to manage clients and create effective programs
                      </Text>
                      
                      <VStack spacing={3} align="start" w="100%">
                        <HStack spacing={3}>
                          <Box w={2} h={2} borderRadius="full" bg="green.500" />
                          <Text fontSize="sm" color="gray.700">
                            <strong>Client Management:</strong> View and manage client profiles and progress
                          </Text>
                        </HStack>
                        <HStack spacing={3}>
                          <Box w={2} h={2} borderRadius="full" bg="green.500" />
                          <Text fontSize="sm" color="gray.700">
                            <strong>Workout Planning:</strong> Create personalized workouts for individuals and groups
                          </Text>
                        </HStack>
                        <HStack spacing={3}>
                          <Box w={2} h={2} borderRadius="full" bg="green.500" />
                          <Text fontSize="sm" color="gray.700">
                            <strong>Group Management:</strong> Create groups, add/remove clients, manage group workouts
                          </Text>
                        </HStack>
                        <HStack spacing={3}>
                          <Box w={2} h={2} borderRadius="full" bg="green.500" />
                          <Text fontSize="sm" color="gray.700">
                            <strong>Exercise Library:</strong> Access and create custom exercises for programs
                          </Text>
                        </HStack>
                      </VStack>
                    </VStack>
                  </VStack>
                </Box>
              </MotionBox>

              {/* Client Features */}
              <MotionBox
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
              >
                <Box
                  {...cardStyles}
                  p={8}
                  borderColor="purple.100"
                  _hover={{ 
                    ...cardStyles._hover,
                    borderColor: "purple.300"
                  }}
                  h="100%"
                >
                  <VStack spacing={6} align="start" h="100%">
                    <Flex
                      w={16}
                      h={16}
                      align="center"
                      justify="center"
                      borderRadius="2xl"
                      bgGradient="linear(135deg, purple.500, purple.600)"
                      color="white"
                      shadow="lg"
                    >
                      <Icon as={FiSmartphone} boxSize={8} />
                    </Flex>
                    
                    <VStack spacing={4} align="start" flex={1}>
                      <Badge colorScheme="purple" variant="subtle" px={3} py={1} borderRadius="full">
                        Client Experience
                      </Badge>
                      
                      <Heading size="lg" color="gray.800" fontWeight="800">
                        Seamless Training Experience
                      </Heading>
                      
                      <Text color="gray.600" lineHeight="1.7" fontSize="md">
                        Simple and intuitive tools to connect with trainers and track progress
                      </Text>
                      
                      <VStack spacing={3} align="start" w="100%">
                        <HStack spacing={3}>
                          <Box w={2} h={2} borderRadius="full" bg="purple.500" />
                          <Text fontSize="sm" color="gray.700">
                            <strong>Trainer Connection:</strong> Connect with trainers using unique TrainerCodes
                          </Text>
                        </HStack>
                        <HStack spacing={3}>
                          <Box w={2} h={2} borderRadius="full" bg="purple.500" />
                          <Text fontSize="sm" color="gray.700">
                            <strong>Mobile Training:</strong> Access workouts and programs via mobile app
                          </Text>
                        </HStack>
                        <HStack spacing={3}>
                          <Box w={2} h={2} borderRadius="full" bg="purple.500" />
                          <Text fontSize="sm" color="gray.700">
                            <strong>Profile Management:</strong> View and manage your fitness profile and preferences
                          </Text>
                        </HStack>
                        <HStack spacing={3}>
                          <Box w={2} h={2} borderRadius="full" bg="purple.500" />
                          <Text fontSize="sm" color="gray.700">
                            <strong>Account Control:</strong> Full control including profile deletion when needed
                          </Text>
                        </HStack>
                      </VStack>
                    </VStack>
                  </VStack>
                </Box>
              </MotionBox>
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>

      {/* Multi-Tenant Role-Based Architecture Section */}
      <Box py={sectionSpacing} px={{ base: 4, md: 8 }} bg="gray.50" position="relative" overflow="hidden">
        {/* Background Decorations */}
        <Box
          position="absolute"
          top="0"
          left="0"
          right="0"
          bottom="0"
          opacity="0.05"
          bgImage="radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.3) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.3) 0%, transparent 50%)"
        />
        
        <Container maxW={containerMaxW} mx="auto" position="relative" zIndex={1}>
          <VStack spacing={16} align="center">
            <VStack spacing={6} textAlign="center" maxW="4xl">
              <Badge 
                bg="purple.100"
                color="purple.700"
                px={6} 
                py={2} 
                borderRadius="full"
                fontSize="md"
                fontWeight="700"
                textTransform="uppercase"
                letterSpacing="wider"
                border="1px solid"
                borderColor="purple.200"
              >
                🏗️ ENTERPRISE ARCHITECTURE
              </Badge>
              <Heading 
                fontSize={{ base: "3xl", md: "4xl", lg: "5xl" }}
                color="gray.800"
                {...headingStyles}
              >
                Multi-Tenant Role-Based
                <Text as="span" color="purple.600" display="block">
                  Security Architecture
                </Text>
              </Heading>
              <Text 
                fontSize={{ base: "lg", md: "xl", lg: "2xl" }}
                color="gray.600" 
                lineHeight="1.7" 
                maxW={{ base: "95%", md: "4xl" }}
                fontWeight="500"
              >
                Secure data isolation, role-based access control, and comprehensive privacy protection for fitness management platforms
              </Text>
              
              {/* Key Features Grid */}
              <SimpleGrid columns={{ base: 2, md: 4 }} spacing={6} mt={8} w="100%">
                <VStack spacing={2}>
                  <Icon as={FiShield} boxSize={8} color="green.500" />
                  <Text fontWeight="bold" fontSize="sm" color="gray.700">Complete Isolation</Text>
                </VStack>
                <VStack spacing={2}>
                  <Icon as={FiLayers} boxSize={8} color="blue.500" />
                  <Text fontWeight="bold" fontSize="sm" color="gray.700">Role-Based Access</Text>
                </VStack>
                <VStack spacing={2}>
                  <Icon as={FiDatabase} boxSize={8} color="purple.500" />
                  <Text fontWeight="bold" fontSize="sm" color="gray.700">Secure Data</Text>
                </VStack>
                <VStack spacing={2}>
                  <Icon as={FiUserCheck} boxSize={8} color="orange.500" />
                  <Text fontWeight="bold" fontSize="sm" color="gray.700">Identity Control</Text>
                </VStack>
              </SimpleGrid>
            </VStack>

            {/* Security & Privacy for Users */}
            <Box w="100%" maxW="1200px" mt={12}>
              <VStack spacing={8}>
                <VStack spacing={4} textAlign="center">
                  <Heading size="lg" color="gray.800">
                    🔒 Your Data Security & Privacy
                  </Heading>
                  <Text fontSize="lg" color="gray.600" maxW="600px">
                    How we protect your personal fitness data and ensure complete privacy
                  </Text>
                </VStack>
                
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} w="100%">
                  <MotionBox
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                  >
                    <Card {...cardStyles} borderColor="green.100" _hover={{...cardStyles._hover, borderColor: "green.300"}}>
                      <CardBody p={6} textAlign="center">
                        <VStack spacing={4}>
                          <Box 
                            p={4} 
                            bg="green.100" 
                            borderRadius="full"
                            border="2px solid"
                            borderColor="green.200"
                          >
                            <Icon as={FiShield} boxSize={8} color="green.600" />
                          </Box>
                          <Heading size="md" color="gray.800">Complete Data Isolation</Heading>
                          <Text color="gray.600" fontSize="sm" lineHeight="1.6">
                            Your personal fitness data is completely isolated from other users. Only you and your assigned trainers can access your information.
                          </Text>
                          <VStack spacing={1} fontSize="xs" color="gray.500">
                            <Text>✓ Secure data transmission</Text>
                            <Text>✓ No cross-user data sharing</Text>
                            <Text>✓ Agency-level isolation</Text>
                          </VStack>
                        </VStack>
                      </CardBody>
                    </Card>
                  </MotionBox>

                  <MotionBox
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                  >
                    <Card {...cardStyles} borderColor="blue.100" _hover={{...cardStyles._hover, borderColor: "blue.300"}}>
                      <CardBody p={6} textAlign="center">
                        <VStack spacing={4}>
                          <Box 
                            p={4} 
                            bg="blue.100" 
                            borderRadius="full"
                            border="2px solid"
                            borderColor="blue.200"
                          >
                            <Icon as={FiUserCheck} boxSize={8} color="blue.600" />
                          </Box>
                          <Heading size="md" color="gray.800">Secure Login & Access</Heading>
                          <Text color="gray.600" fontSize="sm" lineHeight="1.6">
                            Advanced authentication system ensures only you can access your account. Automatic logout and session management keep you secure.
                          </Text>
                          <VStack spacing={1} fontSize="xs" color="gray.500">
                            <Text>✓ JWT token authentication</Text>
                            <Text>✓ Automatic session timeout</Text>
                            <Text>✓ Secure logout everywhere</Text>
                          </VStack>
                        </VStack>
                      </CardBody>
                    </Card>
                  </MotionBox>

                  <MotionBox
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.5 }}
                  >
                    <Card {...cardStyles} borderColor="purple.100" _hover={{...cardStyles._hover, borderColor: "purple.300"}}>
                      <CardBody p={6} textAlign="center">
                        <VStack spacing={4}>
                          <Box 
                            p={4} 
                            bg="purple.100" 
                            borderRadius="full"
                            border="2px solid"
                            borderColor="purple.200"
                          >
                            <Icon as={FiEye} boxSize={8} color="purple.600" />
                          </Box>
                          <Heading size="md" color="gray.800">Controlled Data Access</Heading>
                          <Text color="gray.600" fontSize="sm" lineHeight="1.6">
                            You control who sees your data. Only trainers you connect with via TrainerCode can view your progress and create workouts for you.
                          </Text>
                          <VStack spacing={1} fontSize="xs" color="gray.500">
                            <Text>✓ TrainerCode connection system</Text>
                            <Text>✓ Remove trainers anytime</Text>
                            <Text>✓ Delete account and all data permanently</Text>
                          </VStack>
                        </VStack>
                      </CardBody>
                    </Card>
                  </MotionBox>
                </SimpleGrid>

                {/* How Your Data Stays Safe */}
                <Box w="100%" mt={8} p={8} bg="gradient.100" borderRadius="2xl" border="1px solid" borderColor="blue.100" position="relative" overflow="hidden">
                  <Box
                    position="absolute"
                    top="0"
                    right="0"
                    w="200px"
                    h="200px"
                    bg="blue.500"
                    borderRadius="full"
                    opacity="0.05"
                    transform="translate(50%, -50%)"
                  />
                  <VStack spacing={6} position="relative" zIndex={1}>
                    <VStack spacing={3} textAlign="center">
                      <Heading size="md" color="gray.800">
                        How Your Data Stays Safe
                      </Heading>
                      <Text color="gray.600" maxW="600px">
                        Every time you use the app, your data goes through multiple security checks
                      </Text>
                    </VStack>
                    
                    <HStack spacing={6} align="center" flexWrap="wrap" justify="center">
                      <VStack spacing={2}>
                        <Box p={3} bg="blue.500" borderRadius="lg" color="white" shadow="md">
                          <Icon as={FiLock} boxSize={6} />
                        </Box>
                        <VStack spacing={0}>
                          <Text fontWeight="bold" color="gray.800" fontSize="sm">Secure Login</Text>
                          <Text fontSize="xs" color="gray.500" textAlign="center">Your credentials are verified</Text>
                        </VStack>
                      </VStack>
                      
                      <Icon as={FiArrowRight} boxSize={5} color="blue.400" />
                      
                      <VStack spacing={2}>
                        <Box p={3} bg="green.500" borderRadius="lg" color="white" shadow="md">
                          <Icon as={FiShield} boxSize={6} />
                        </Box>
                        <VStack spacing={0}>
                          <Text fontWeight="bold" color="gray.800" fontSize="sm">Permission Check</Text>
                          <Text fontSize="xs" color="gray.500" textAlign="center">Only you can see your data</Text>
                        </VStack>
                      </VStack>
                      
                      <Icon as={FiArrowRight} boxSize={5} color="blue.400" />
                      
                      <VStack spacing={2}>
                        <Box p={3} bg="purple.500" borderRadius="lg" color="white" shadow="md">
                          <Icon as={FiDatabase} boxSize={6} />
                        </Box>
                        <VStack spacing={0}>
                          <Text fontWeight="bold" color="gray.800" fontSize="sm">Data Filtering</Text>
                          <Text fontSize="xs" color="gray.500" textAlign="center">Isolated from other users</Text>
                        </VStack>
                      </VStack>
                      
                      <Icon as={FiArrowRight} boxSize={5} color="blue.400" />
                      
                      <VStack spacing={2}>
                        <Box p={3} bg="orange.500" borderRadius="lg" color="white" shadow="md">
                          <Icon as={FiUser} boxSize={6} />
                        </Box>
                        <VStack spacing={0}>
                          <Text fontWeight="bold" color="gray.800" fontSize="sm">Your Data</Text>
                          <Text fontSize="xs" color="gray.500" textAlign="center">Safe & private</Text>
                        </VStack>
                      </VStack>
                    </HStack>
                    
                    <Box p={4} bg="blue.50" borderRadius="lg" maxW="500px">
                      <Text fontSize="sm" color="blue.700" textAlign="center" fontWeight="medium">
                        💡 Your fitness data is securely transmitted and completely isolated between agencies - only your connected trainers can access your workout data
                      </Text>
                    </Box>
                  </VStack>
                </Box>
              </VStack>
            </Box>
          </VStack>
        </Container>
      </Box>

      {/* Platform Access Section */}
      <Box py={sectionSpacing} px={{ base: 4, md: 8 }} bg="white" position="relative">
        <Container maxW={containerMaxW}>
          <VStack spacing={16} textAlign="center">
            {/* Enhanced Section Header */}
            <VStack spacing={6} position="relative">
              {/* Decorative Background Elements */}
              <Box
                position="absolute"
                top="-20px"
                left="50%"
                transform="translateX(-50%)"
                w="100px"
                h="100px"
                bgGradient="radial(circle, blue.100 0%, transparent 70%)"
                borderRadius="full"
                opacity="0.6"
                zIndex={0}
              />
              
              <VStack spacing={4} position="relative" zIndex={1}>
                <Badge 
                  bg="blue.100"
                  color="blue.700"
                  px={6} 
                  py={2} 
                  borderRadius="full"
                  fontSize="md"
                  fontWeight="700"
                  textTransform="uppercase"
                  letterSpacing="wider"
                  border="1px solid"
                  borderColor="blue.200"
                >
                  📱 MULTI-PLATFORM ACCESS
                </Badge>
                
                <Heading 
                  fontSize={{ base: "3xl", md: "4xl", lg: "5xl" }}
                  color="gray.800"
                  {...headingStyles}
                  textAlign="center"
                  maxW="4xl"
                >
                  Access TrackHQ
                  <Text as="span" display="block" color="blue.600">
                    Anywhere, Anytime
                  </Text>
                </Heading>
                
                <Text 
                  fontSize={{ base: "lg", md: "xl" }}
                  color="gray.600" 
                  maxW="4xl"
                  lineHeight="1.7"
                  textAlign="center"
                  fontWeight="400"
                >
                  Choose your preferred platform and experience TrackHQ's powerful fitness management tools 
                  designed specifically for your role
                </Text>
              </VStack>
            </VStack>

            {/* Platform Links Grid */}
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8} w="100%" maxW="4xl">
              {/* Mobile App - iOS */}
              <Box
                {...cardStyles}
                p={6}
                textAlign="center"
              >
                <VStack spacing={4}>
                  <Flex
                    w={16}
                    h={16}
                    align="center"
                    justify="center"
                    borderRadius="lg"
                    bg="gray.100"
                  >
                    <Icon as={FaApple} boxSize={8} color="gray.700" />
                  </Flex>
                  
                  <HStack spacing={2}>
                    <Badge colorScheme="blue" fontSize="xs">
                      Trainers
                    </Badge>
                    <Badge colorScheme="green" fontSize="xs">
                      Clients
                    </Badge>
                  </HStack>
                  
                  <VStack spacing={2}>
                    <Heading size="md" color="gray.800">
                      iOS App
                    </Heading>
                    <Text color="gray.600" fontSize="sm">
                      Download from App Store for iPhone and iPad
                    </Text>
                  </VStack>
                  
                  <Button
                    as={Link}
                    href="https://apps.apple.com/app/trackhq/id6754441500"
                    colorScheme="gray"
                    variant="solid"
                    size="md"
                    leftIcon={<Icon as={FaApple} />}
                    w="full"
                    isExternal
                  >
                    App Store
                  </Button>
                </VStack>
              </Box>

              {/* Mobile App - Android */}
              <Box
                {...cardStyles}
                p={6}
                textAlign="center"
              >
                <VStack spacing={4}>
                  <Flex
                    w={16}
                    h={16}
                    align="center"
                    justify="center"
                    borderRadius="lg"
                    bg="green.100"
                  >
                    <Icon as={FaGooglePlay} boxSize={8} color="green.600" />
                  </Flex>
                  
                  <HStack spacing={2}>
                    <Badge colorScheme="blue" fontSize="xs">
                      Trainers
                    </Badge>
                    <Badge colorScheme="green" fontSize="xs">
                      Clients
                    </Badge>
                  </HStack>
                  
                  <VStack spacing={2}>
                    <Heading size="md" color="gray.800">
                      Android App
                    </Heading>
                    <Text color="gray.600" fontSize="sm">
                      Download from Google Play Store for Android devices
                    </Text>
                  </VStack>
                  
                  <Button
                    as={Link}
                    href="https://play.google.com/store/apps/details?id=com.speeir.trackhq"
                    colorScheme="green"
                    variant="solid"
                    size="md"
                    leftIcon={<Icon as={FaGooglePlay} />}
                    w="full"
                    isExternal
                  >
                    Google Play
                  </Button>
                </VStack>
              </Box>

              {/* Web Platform */}
              <Box
                {...cardStyles}
                p={6}
                textAlign="center"
              >
                <VStack spacing={4}>
                  <Flex
                    w={16}
                    h={16}
                    align="center"
                    justify="center"
                    borderRadius="lg"
                    bg="purple.100"
                  >
                    <Icon as={FiGlobe} boxSize={8} color="purple.600" />
                  </Flex>
                  
                  <HStack spacing={2}>
                    <Badge colorScheme="purple" fontSize="xs">
                      Admins
                    </Badge>
                    <Badge colorScheme="orange" fontSize="xs">
                      Agencies
                    </Badge>
                  </HStack>
                  
                  <VStack spacing={2}>
                    <Heading size="md" color="gray.800">
                      Web Platform
                    </Heading>
                    <Text color="gray.600" fontSize="sm">
                      Full management dashboard via browser
                    </Text>
                  </VStack>
                  
                  <Button
                    as={Link}
                    href="/auth/sign-in"
                    colorScheme="purple"
                    variant="solid"
                    size="md"
                    leftIcon={<Icon as={FiGlobe} />}
                    w="full"
                  >
                    Web Access
                  </Button>
                </VStack>
              </Box>
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>

      {/* Footer */}
      <Box 
        bg="gray.900" 
        color="white" 
        py={{ base: 10, md: 12 }} 
        px={{ base: 4, md: 8 }}
      >
        <Container maxW="1200px" mx="auto">
          <VStack spacing={{ base: 6, md: 8 }} textAlign="center">
            {/* Brand */}
            <VStack spacing={3}>
              <Heading 
                fontSize={{ base: "xl", md: "2xl" }}
                fontWeight="800"
                bgGradient="linear(135deg, #3b82f6 0%, #6366f1 100%)"
                bgClip="text"
                letterSpacing="-0.02em"
              >
                TrackHQ
              </Heading>
              <Text 
                color="gray.400" 
                fontSize={{ base: "sm", md: "md" }}
                maxW="400px"
                lineHeight="1.6"
              >
                Professional fitness management made accessible
              </Text>
            </VStack>

            {/* Links */}
            <HStack 
              spacing={{ base: 4, md: 8 }}
              flexWrap="wrap" 
              justify="center"
              color="gray.400" 
              fontSize={{ base: "sm", md: "md" }}
            >
              <Link 
                href="https://trackhq.speeir.com" 
                isExternal 
                _hover={{ color: "blue.400" }}
                transition="color 0.2s ease"
              >
                Platform
              </Link>
              <Link 
                href="https://www.speeir.com" 
                isExternal 
                _hover={{ color: "blue.400" }}
                transition="color 0.2s ease"
              >
                Speeir Limited
              </Link>
              <Link 
                href="mailto:support@speeir.com" 
                _hover={{ color: "blue.400" }}
                transition="color 0.2s ease"
              >
                Support
              </Link>
            </HStack>

            {/* Copyright */}
            <Divider borderColor="gray.700" />
            <Text fontSize={{ base: "xs", md: "sm" }} color="gray.500">
              © 2025 Speeir Limited. All rights reserved.
            </Text>
          </VStack>
        </Container>
      </Box>
    </Box>
  );
}

export default PublicGettingStarted;